import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle, AlertCircle, Clock, TrendingUp, TrendingDown,
  RefreshCw, Plus, X
} from 'lucide-react';
import api from '../utils/api';
import { formatCurrency, formatDate, isAtrasado, statusEfetivo } from '../utils/format';
import { useTenant } from '../contexts/TenantContext';
import StatusBadge from '../components/StatusBadge';

const TIPO_COLORS = { RECEITA: '#339933', DESPESA: '#d72d21', CUSTO: '#c99a1a' };

/* ─── KPI card ───────────────────────────────────────────── */
function KPICard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="fin-card p-3 sm:p-5 min-w-0">
      <div className="flex items-center justify-between mb-1.5 sm:mb-3">
        <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-wide sm:tracking-widest truncate">{label}</p>
        <div className="hidden sm:flex w-9 h-9 rounded-xl items-center justify-center" style={{ background: `${color}18` }}>
          <Icon size={16} style={{ color }} />
        </div>
      </div>
      <p className="text-xs min-[400px]:text-sm sm:text-2xl font-bold whitespace-nowrap tracking-tight" style={{ color, fontFamily: 'Manrope, sans-serif' }}>
        {formatCurrency(value)}
      </p>
      {sub && <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5 sm:mt-1 truncate">{sub}</p>}
    </div>
  );
}

/* ─── modal novo lançamento (a pagar ou a receber) ──────── */
const today = new Date().toISOString().split('T')[0];

const emptyForm = {
  descricao: '',
  tipo: 'DESPESA',
  valor: '',
  dataCompetencia: today,
  categoria: '',
  status: 'pendente',
  carteiraId: '',
  formaPagamento: '',
};

function NovoLancamentoModal({ open, onClose, onSaved, defaultTipo }) {
  const [form, setForm] = useState({ ...emptyForm, tipo: defaultTipo || 'DESPESA' });
  const [carteiras, setCarteiras] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, tipo: defaultTipo || 'DESPESA' });
      setError('');
      api.get('/carteiras').then(r => setCarteiras(r.data)).catch(() => {});
    }
  }, [open, defaultTipo]);

  if (!open) return null;

  const carteiraSelected = carteiras.find(c => c.id === form.carteiraId);

  const handleSave = async () => {
    if (!form.descricao.trim()) { setError('Descrição é obrigatória.'); return; }
    const val = parseFloat(form.valor.replace(',', '.'));
    if (!val || val <= 0) { setError('Valor inválido.'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        descricao: form.descricao.trim(),
        tipo: form.tipo,
        valor: val,
        dataCompetencia: form.dataCompetencia,
        categoria: form.categoria || null,
        status: form.status,
        formaPagamento: form.formaPagamento || null,
        carteiraId: form.carteiraId || null,
        carteiraNome: carteiraSelected?.nome || null,
        carteiraCor: carteiraSelected?.cor || null,
      };
      await api.post('/lancamentos', payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const tipoOpts = [
    { value: 'DESPESA', label: 'Despesa (a pagar)',  color: '#d72d21' },
    { value: 'RECEITA', label: 'Receita (a receber)', color: '#339933' },
    { value: 'CUSTO',   label: 'Custo',               color: '#c99a1a' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 nexus-modal-overlay">
      <div className="nexus-modal w-full max-w-md max-h-[92vh] flex flex-col">
        {/* header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
          <h2 className="font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Novo Lançamento
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold leading-none">
            <X size={18} />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {/* Tipo */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tipo *</label>
            <div className="grid grid-cols-3 gap-2">
              {tipoOpts.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, tipo: opt.value }))}
                  className="py-2 px-2 rounded-xl border-2 text-xs font-bold transition-all"
                  style={form.tipo === opt.value
                    ? { borderColor: opt.color, background: `${opt.color}12`, color: opt.color }
                    : { borderColor: '#e2e8f0', color: '#94a3b8' }
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Descrição *</label>
            <input
              className="fin-input w-full"
              placeholder="Ex: Aluguel, Fornecedor, Salário..."
              value={form.descricao}
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
            />
          </div>

          {/* Valor + Data */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Valor (R$) *</label>
              <input
                className="fin-input w-full"
                placeholder="0,00"
                value={form.valor}
                onChange={e => setForm(f => ({ ...f, valor: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Vencimento *</label>
              <input
                type="date"
                className="fin-input w-full"
                value={form.dataCompetencia}
                onChange={e => setForm(f => ({ ...f, dataCompetencia: e.target.value }))}
              />
            </div>
          </div>

          {/* Categoria + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Categoria</label>
              <input
                className="fin-input w-full"
                placeholder="Ex: Moradia..."
                value={form.categoria}
                onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Status</label>
              <select className="fin-input w-full" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="pendente">Pendente</option>
                <option value="atrasado">Atrasado</option>
              </select>
            </div>
          </div>

          {/* Carteira */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Carteira</label>
            {carteiras.length === 0 ? (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                Nenhuma carteira cadastrada. Acesse <strong>Carteira</strong> para adicionar.
              </p>
            ) : (
              <select
                className="fin-input w-full"
                value={form.carteiraId}
                onChange={e => setForm(f => ({ ...f, carteiraId: e.target.value }))}
              >
                <option value="">— Sem carteira —</option>
                {carteiras.map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            )}
            {carteiraSelected && (
              <div className="flex items-center gap-2 mt-1.5">
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-white text-xs font-semibold"
                  style={{ background: carteiraSelected.cor || '#64748b' }}
                >
                  {carteiraSelected.nome}
                </span>
              </div>
            )}
          </div>

          {/* Forma de pagamento */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Forma de Pagamento</label>
            <select className="fin-input w-full" value={form.formaPagamento} onChange={e => setForm(f => ({ ...f, formaPagamento: e.target.value }))}>
              <option value="">— Selecionar —</option>
              <option value="pix">PIX</option>
              <option value="boleto">Boleto</option>
              <option value="cartao_credito">Cartão de Crédito</option>
              <option value="cartao_debito">Cartão de Débito</option>
              <option value="transferencia">Transferência</option>
              <option value="dinheiro">Dinheiro</option>
            </select>
          </div>
        </div>

        {/* footer */}
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-3 flex-shrink-0">
          <button onClick={onClose} className="fin-btn-secondary">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="fin-btn-primary">
            {saving ? 'Salvando...' : 'Lançar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── main page ──────────────────────────────────────────── */
export default function ContasPagar() {
  const { tenant } = useTenant();
  const accent = tenant?.corPrimaria || '#0147a0';

  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('todos');
  const [markingId, setMarkingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTipo, setModalTipo] = useState('DESPESA');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pendRes, atrasRes] = await Promise.all([
        api.get('/lancamentos?status=pendente'),
        api.get('/lancamentos?status=atrasado'),
      ]);
      const todos = [...pendRes.data, ...atrasRes.data];
      const unique = Object.values(Object.fromEntries(todos.map(l => [l.id, l])));
      unique.sort((a, b) => (a.dataPagamento || a.dataCompetencia).localeCompare(b.dataPagamento || b.dataCompetencia));
      setLancamentos(unique);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const marcarPago = async (id) => {
    setMarkingId(id);
    try {
      await api.put(`/lancamentos/${id}`, { status: 'pago', dataPagamento: new Date().toISOString().split('T')[0] });
      setLancamentos(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      console.error(err);
    } finally {
      setMarkingId(null);
    }
  };

  const openModal = (tipo) => { setModalTipo(tipo); setModalOpen(true); };

  const atrasados = lancamentos.filter(l => isAtrasado(l));
  const aReceber   = lancamentos.filter(l => l.tipo === 'RECEITA');
  const aPagar     = lancamentos.filter(l => l.tipo !== 'RECEITA');

  const totalReceber  = aReceber.reduce((s, l) => s + l.valor, 0);
  const totalPagar    = aPagar.reduce((s, l) => s + l.valor, 0);
  const totalAtrasado = atrasados.reduce((s, l) => s + l.valor, 0);

  const tabList = [
    { id: 'todos',    label: `Todos (${lancamentos.length})` },
    { id: 'receber',  label: `A Receber (${aReceber.length})` },
    { id: 'pagar',    label: `A Pagar (${aPagar.length})` },
    { id: 'atrasados',label: `Atrasados (${atrasados.length})` },
  ];

  const [searchParams, setSearchParams] = useSearchParams();
  const urlBusca = searchParams.get('busca') || '';

  const displayedBase = tab === 'receber'   ? aReceber
    : tab === 'pagar'     ? aPagar
    : tab === 'atrasados' ? atrasados
    : lancamentos;

  const displayed = urlBusca
    ? displayedBase.filter(l =>
        l.descricao?.toLowerCase().includes(urlBusca.toLowerCase()) ||
        l.categoria?.toLowerCase().includes(urlBusca.toLowerCase())
      )
    : displayedBase;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Banner de filtro ativo da pesquisa global */}
      {urlBusca && (
        <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-xl text-xs text-blue-700 dark:text-blue-300">
          <span>Filtrando contas por: <strong>"{urlBusca}"</strong> ({displayed.length} encontradas)</span>
          <button
            onClick={() => setSearchParams({})}
            className="font-bold underline hover:text-blue-900 dark:hover:text-blue-100"
          >
            Limpar filtro
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Contas a Pagar / Receber
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Lançamentos pendentes e atrasados em aberto.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={load} className="fin-btn-secondary text-xs" disabled={loading}>
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Atualizar
          </button>
          <button
            onClick={() => openModal('RECEITA')}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border-2 border-emerald-500 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
          >
            <Plus size={13} /> A Receber
          </button>
          <button
            onClick={() => openModal('DESPESA')}
            className="fin-btn-primary text-xs flex items-center gap-1.5"
            style={{ background: accent }}
          >
            <Plus size={13} /> A Pagar
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <KPICard label="A Receber" value={totalReceber} icon={TrendingUp}    color="#339933" sub={`${aReceber.length} lançamento${aReceber.length !== 1 ? 's' : ''}`} />
        <KPICard label="A Pagar"   value={totalPagar}   icon={TrendingDown}  color="#d72d21" sub={`${aPagar.length} lançamento${aPagar.length !== 1 ? 's' : ''}`} />
        <KPICard label="Atrasados" value={totalAtrasado} icon={AlertCircle}  color="#ef4444" sub={`${atrasados.length} vencido${atrasados.length !== 1 ? 's' : ''}`} />
      </div>

      {/* Alert banner */}
      {atrasados.length > 0 && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
          <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 font-medium">
            Você tem <strong>{atrasados.length}</strong> lançamento{atrasados.length !== 1 ? 's' : ''} atrasado{atrasados.length !== 1 ? 's' : ''} no total de {formatCurrency(totalAtrasado)}.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        {tabList.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 sm:flex-none whitespace-nowrap px-1 sm:px-4 py-2.5 text-[11px] sm:text-sm font-semibold border-b-2 transition-colors ${
              tab === t.id ? 'border-current' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
            style={tab === t.id ? { color: accent, borderColor: accent } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="fin-card">
        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Carregando...</div>
        ) : displayed.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle size={32} className="mx-auto mb-3 text-emerald-400" />
            <p className="font-semibold text-slate-600">Tudo em dia!</p>
            <p className="text-sm text-slate-400 mt-1">Nenhum lançamento pendente nesta categoria.</p>
            <div className="flex justify-center gap-3 mt-4">
              <button
                onClick={() => openModal('RECEITA')}
                className="text-xs font-semibold px-3 py-1.5 rounded-xl border-2 border-emerald-500 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
              >
                + A Receber
              </button>
              <button
                onClick={() => openModal('DESPESA')}
                className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-slate-800 text-white hover:bg-slate-700 transition-colors"
              >
                + A Pagar
              </button>
            </div>
          </div>
        ) : (
          <>
          {/* Celular: um cartão por conta, com o botão "Marcar pago" sempre à vista */}
          <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800" data-testid="contas-cards">
            {displayed.map(l => {
              const atras = isAtrasado(l);
              return (
                <div key={l.id} className={`p-4 space-y-2.5 ${atras ? 'bg-red-50/30 dark:bg-red-500/5' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{l.descricao}</p>
                      <p className="text-xs text-slate-400 truncate">{l.categoria || '-'}</p>
                    </div>
                    <p className="text-sm font-bold whitespace-nowrap" style={{ color: TIPO_COLORS[l.tipo] }}>{formatCurrency(l.valor)}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    {atras ? <AlertCircle size={13} className="text-red-500" /> : <Clock size={13} className="text-amber-500" />}
                    <span className={`font-semibold ${atras ? 'text-red-600' : 'text-slate-600 dark:text-slate-300'}`}>{formatDate(l.dataPagamento || l.dataCompetencia)}</span>
                    <StatusBadge type="tipo" value={l.tipo} />
                    <StatusBadge type="status" value={statusEfetivo(l)} />
                    {l.carteiraNome && (
                      <span className="px-2 py-0.5 rounded-full text-white font-semibold truncate max-w-[110px]" style={{ background: l.carteiraCor || '#64748b', fontSize: '10px' }}>{l.carteiraNome}</span>
                    )}
                  </div>
                  <button
                    onClick={() => marcarPago(l.id)}
                    disabled={markingId === l.id}
                    className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 disabled:opacity-50"
                  >
                    <CheckCircle size={13} />
                    {markingId === l.id ? 'Salvando...' : 'Marcar pago'}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Vencimento', 'Descrição', 'Tipo', 'Carteira', 'Valor', 'Status', ''].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {displayed.map(l => (
                  <tr key={l.id} className={`hover:bg-slate-50 transition-colors ${isAtrasado(l) ? 'bg-red-50/30' : ''}`}>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {isAtrasado(l)
                          ? <AlertCircle size={13} className="text-red-500 flex-shrink-0" />
                          : <Clock size={13} className="text-amber-500 flex-shrink-0" />
                        }
                        <span className={`text-xs font-semibold ${isAtrasado(l) ? 'text-red-600' : 'text-slate-600'}`}>
                          {formatDate(l.dataPagamento || l.dataCompetencia)}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-medium text-slate-800 max-w-[200px]">
                      <span className="truncate block">{l.descricao}</span>
                      <span className="text-xs text-slate-400">{l.categoria || '-'}</span>
                    </td>
                    <td className="px-5 py-3"><StatusBadge type="tipo" value={l.tipo} /></td>
                    <td className="px-5 py-3">
                      {l.carteiraNome ? (
                        <span className="inline-flex items-center justify-center w-44 px-2 py-0.5 rounded-full text-white font-semibold whitespace-nowrap truncate"
                          style={{ background: l.carteiraCor || '#64748b', fontSize: '10px' }}>
                          {l.carteiraNome}
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-5 py-3 font-bold whitespace-nowrap" style={{ color: TIPO_COLORS[l.tipo] }}>
                      {formatCurrency(l.valor)}
                    </td>
                    <td className="px-5 py-3"><StatusBadge type="status" value={statusEfetivo(l)} /></td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => marcarPago(l.id)}
                        disabled={markingId === l.id}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        <CheckCircle size={12} />
                        {markingId === l.id ? 'Salvando...' : 'Marcar pago'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Modal */}
      <NovoLancamentoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        defaultTipo={modalTipo}
      />
    </div>
  );
}
